import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BookOpen,
  Filter,
  Layers,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Upload,
  Zap,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import KPITile from "../components/KPITile";
import StatusBadge from "../components/StatusBadge";
import UploadStandardModal from "./UploadStandardModal";
import SectionTreeView from "./SectionTreeView";
import ConfirmDialog from "./ConfirmDialog";
import StandardWorkspace from "./StandardWorkspace";
import { createStandard, deleteStandard, extractStandard, listStandards } from "./api";
import { ConfirmedStandard, IngestState, VerityStandard } from "./types";

const ALL = "All";

/** The ingest state as a badge. `uploaded` is deliberately a warning: the row exists, the rules do not. */
const STATE_BADGE: Record<IngestState, { status: "pass" | "warning" | "fail" | "info"; label: string }> = {
  uploaded: { status: "warning", label: "Not extracted" },
  extracting: { status: "info", label: "Extracting" },
  extracted: { status: "pass", label: "Structured" },
  failed: { status: "fail", label: "Failed" },
};

/**
 * The Standards & Frameworks Library.
 *
 * Kept as the prototype had it — a flat, faceted table rather than a jurisdiction tree. The path is
 * stored structurally and can be filtered on, but a tree with one leaf is a worse screen than a list,
 * and the library will be a list long before it is a forest.
 *
 * TWO AUDIENCES, ONE SCREEN. Building the library — uploading, extracting, reviewing requirements,
 * deleting — is administration. Everyone else needs to see what the compliance engine is checking
 * against, which is reading. `readOnly` is that second audience: the same list and the same
 * structure, with every control that writes taken away rather than disabled, because a row of
 * greyed-out buttons on a screen you will never be allowed to use is just clutter.
 */
interface StandardsLibraryProps {
  /** View only: no upload, no extract, no delete, no requirements workbench. */
  readOnly?: boolean;
  /**
   * Which side of the mode switch is showing. The library holds only that side's standards, and a
   * standard added here is added to that side.
   */
  reviewType?: "engineering" | "awp";
}

const StandardsLibrary = ({ readOnly = false, reviewType = "engineering" }: StandardsLibraryProps) => {
  const [standards, setStandards] = useState<VerityStandard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [domain, setDomain] = useState(ALL);
  /** The "what this holds" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /**
   * The standard being worked on. The catalogue stays a catalogue; this is the workbench.
   *
   * IN THE URL, for the same two reasons the open project is: a refresh inside the workbench used
   * to drop you back on the catalogue, and the sidebar could not see this, so pressing "Manage
   * Standards" while inside a standard did nothing — the tab was already `standards`. As a
   * parameter it is something the sidebar can clear. See NESTED_PARAMS in useNavTab.
   */
  const [params, setParams] = useSearchParams();
  const workspaceId = params.get("standard");
  const setWorkspaceId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) next.set("standard", id);
      else next.delete("standard");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  /** The workbench we were in last render, so leaving it can be told from arriving. */
  const leftRef = useRef<string | null>(null);
  /** The one standard that has just been added, and so gets the walk through its four screens. */
  const [introduceId, setIntroduceId] = useState<string | null>(null);
  /** A destructive or slow action waiting to be confirmed. */
  const [confirming, setConfirming] = useState<
    { kind: "delete" | "extract"; standard: VerityStandard } | null
  >(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // Filtered once, here, so the counts, the domain chips and the open workbench all see only this
      // side's standards. Absent reviewType is engineering — see VerityStandard.
      const side = (s: VerityStandard) => (s.reviewType === "awp" ? "awp" : "engineering");
      setStandards((await listStandards()).filter((s) => side(s) === reviewType));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the standards library.");
      setStandards([]);
    }
  }, [reviewType]);

  useEffect(() => {
    void load();
  }, [load]);

  const domains = useMemo(() => {
    const found = new Set((standards ?? []).map((s) => s.domain).filter(Boolean));
    return [ALL, ...[...found].sort()];
  }, [standards]);

  const visible = useMemo(
    () => (standards ?? []).filter((s) => domain === ALL || s.domain === domain),
    [standards, domain],
  );

  const counts = useMemo(() => {
    const rows = standards ?? [];
    return {
      total: rows.length,
      // "Uploaded" counts documents the library actually holds, not rows in it. A reference row is a
      // name; counting it here would overstate what can be checked.
      uploaded: rows.filter((s) => s.type === "ingested").length,
      reference: rows.filter((s) => s.type === "reference").length,
      domains: new Set(rows.map((s) => s.domain).filter(Boolean)).size,
    };
  }, [standards]);

  // The source document is reached through the standard's own Source tab and from the citation on
  // every requirement. Duplicating that in the catalogue's action bar bought nothing.
  const openUpload = (file: File | null) => {
    setDroppedFile(file);
    setUploadOpen(true);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) openUpload(file);
  };

  const submitUpload = async (confirmed: ConfirmedStandard) => {
    const created = await createStandard(
      reviewType === "awp" ? { ...confirmed, reviewType: "awp" } : confirmed,
    );
    setUploadOpen(false);
    setDroppedFile(null);
    setStandards((prev) => [...(prev ?? []), created]);
    // A confirmed standard arrives structured, so open its tree rather than making them hunt for it —
    // and walk the four screens on the way in, since this is the first time anyone has seen them.
    if (created.state === "extracted") {
      setIntroduceId(created.id);
      setWorkspaceId(created.id);
    }
  };

  const runExtract = async (standard: VerityStandard) => {
    setBusyId(standard.id);
    setError(null);
    try {
      const updated = await extractStandard(standard.id);
      setStandards((prev) => (prev ?? []).map((s) => (s.id === updated.id ? updated : s)));
      setOpenId(updated.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Extraction failed.");
      // The record carries the failure too, so pull it back rather than leaving the row optimistic.
      void load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (standard: VerityStandard) => {
    setBusyId(standard.id);
    setError(null);
    try {
      await deleteStandard(standard.id);
      setStandards((prev) => (prev ?? []).filter((s) => s.id !== standard.id));
      if (openId === standard.id) setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the standard.");
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Tidy up on the way OUT of the workbench, however it was left.
   *
   * The Back button used to do this itself, which was enough while Back was the only way out. Now
   * the sidebar can also clear the parameter, and that path has no callback to hang it on — so it
   * keys off the transition instead: the catalogue is reloaded because the workbench may have
   * changed a standard, and the introduction is dropped so re-opening the same standard later does
   * not walk through it again. Only on leaving, never on arriving, or the first render would fire a
   * second load on top of the one that fetches the catalogue.
   */
  useEffect(() => {
    const left = leftRef.current !== null && workspaceId === null;
    leftRef.current = workspaceId;
    if (!left) return;
    setIntroduceId(null);
    void load();
  }, [workspaceId, load]);

  const workspace = (standards ?? []).find((s) => s.id === workspaceId);
  if (workspace) {
    return (
      <StandardWorkspace
        standard={workspace}
        readOnly={readOnly}
        introduce={workspace.id === introduceId}
        onBack={() => setWorkspaceId(null)}
      />
    );
  }

  return (
    <div className="vr-page">
      <div className="vr-page-head">
        <div>
          {/* Worth reading once. Left permanently under the heading it pushes the counts and the
              library itself down the screen on every visit. Same "i" toggle Findings uses. */}
          <h1 className="vr-h-page">
            Standards &amp; Frameworks Library
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this holds"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            {readOnly
              ? "The governing standards IQ Verity checks submissions against. Each one has been extracted into a structured source layer — sections, pages and citations — so every finding can cite the clause it came from."
              : "Governing standards ingested by the IQ Verity compliance engine. Uploaded standards are extracted into a structured source layer — sections, pages and citations — before any requirement is interpreted from them."}
          </p>
        </div>
        {!readOnly && (
          <button className="vr-btn vr-btn-glow" onClick={() => openUpload(null)}>
            <Upload size={15} /> Upload Standard
          </button>
        )}
      </div>

      <div className="vr-grid vr-grid-4">
        <KPITile icon={BookOpen} label="Total Standards" value={counts.total} />
        <KPITile icon={Upload} label="Uploaded" value={counts.uploaded} />
        <KPITile icon={ShieldCheck} label="Reference Frameworks" value={counts.reference} />
        <KPITile icon={Filter} label="Domains" value={counts.domains} />
      </div>

      {!readOnly && (
      <div
        className={`vr-drop ${dragging ? "vr-dragging" : ""}`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Upload size={26} className="vr-drop-icon" />
        <span className="vr-drop-title">Drag and drop a standards document, or click to browse</span>
        <span className="vr-drop-sub">
          PDF — municipal development standards, specifications, codes and regulatory texts
        </span>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) openUpload(file);
            e.target.value = "";
          }}
        />
      </div>
      )}

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-chips">
        {domains.map((d) => (
          <button
            key={d}
            className={`vr-chip-btn ${domain === d ? "vr-active" : ""}`}
            onClick={() => setDomain(d)}
          >
            {d}
          </button>
        ))}
      </div>

      <GlowCard className="vr-table-card" hover={false}>
        <div className="vr-table-head">
          <h3>Governing Standards ({visible.length})</h3>
          <button className="vr-rowbtn" onClick={() => void load()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        <div className="vr-table-scroll">
          <table className="vr-table">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Domain</th>
                <th>Issuer</th>
                <th>Edition</th>
                <th>Jurisdiction</th>
                <th>Structure</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((standard) => {
                const badge = STATE_BADGE[standard.state];
                const busy = busyId === standard.id;
                const open = openId === standard.id;
                return (
                  <React.Fragment key={standard.id}>
                  <tr className={openId === standard.id ? "vr-row-open" : ""}>
                    <td className="vr-cell-title">
                      <div>
                        <strong>{standard.title}</strong>
                        <div className="vr-cell-code">
                          {standard.shortCode || standard.fileName || "—"}
                        </div>
                      </div>
                    </td>
                    <td className="vr-cell-dim">{standard.domain}</td>
                    <td className="vr-cell-dim">{standard.authority}</td>
                    <td className="vr-cell-dim">{standard.edition}</td>
                    <td className="vr-cell-dim">{standard.jurisdictionPath.join(" › ")}</td>
                    <td>
                      {standard.type === "reference" ? (
                        <span className="vr-cell-dim">—</span>
                      ) : (
                        <>
                          <StatusBadge status={badge.status} label={badge.label} />
                          {standard.extraction && (
                            <div className="vr-cell-code">
                              {standard.extraction.sectionCount} sections ·{" "}
                              {standard.extraction.pageCount} pages
                              {standard.extraction.rasterPageCount > 0 &&
                                ` · ${standard.extraction.rasterPageCount} raster`}
                            </div>
                          )}
                          {standard.state === "failed" && standard.extractionError && (
                            <div className="vr-cell-code">{standard.extractionError}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {/* Marks, not captions. Three words per row across a library of standards is a
                          column of repeated text; what each one does lives on hover. */}
                      <div className="vr-cell-actions">
                        {/* Opening a standard is reading it, so it is offered to everyone — the
                            workbench itself drops the controls that build the library. */}
                        {standard.state === "extracted" && (
                          <button
                            className={`vr-rowbtn vr-icononly ${open ? "vr-on" : ""}`}
                            onClick={() => setWorkspaceId(standard.id)}
                            title="Open this standard — structure, requirements and source"
                            aria-label="Open standard"
                          >
                            <Layers size={13} />
                          </button>
                        )}
                        {!readOnly && standard.type === "ingested" && (
                          <button
                            className="vr-rowbtn vr-icononly"
                            onClick={() =>
                              standard.state === "extracted"
                                ? setConfirming({ kind: "extract", standard })
                                : void runExtract(standard)
                            }
                            disabled={busy}
                            title={
                              standard.state === "extracted"
                                ? "Re-extract — rebuild the canonical text and section tree"
                                : "Extract the canonical text and build the section tree"
                            }
                            aria-label={standard.state === "extracted" ? "Re-extract" : "Extract"}
                          >
                            {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Zap size={13} />}
                          </button>
                        )}
                        {!readOnly && (
                          <button
                            className="vr-rowbtn vr-icononly vr-danger"
                            onClick={() => setConfirming({ kind: "delete", standard })}
                            disabled={busy}
                            title="Remove from the library"
                            aria-label="Remove"
                          >
                            {/* Removing a fully-processed standard deletes thousands of records.
                                Without a spinner the button looked dead and invited a second click. */}
                            {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
                          </button>
                        )}
                        {/* Reading the structure is reading, so it stays — the expander below the
                            row opens the section tree without reaching the workbench. */}
                        {readOnly && standard.state !== "extracted" && (
                          <span className="vr-cell-dim">—</span>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* The structure opens in place, under the standard it belongs to. */}
                  {open && standard.state === "extracted" && (
                    <tr className="vr-row-detail">
                      <td colSpan={7}>
                        <SectionTreeView standard={standard} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {standards === null && (
          <div className="vr-empty">
            <Loader2 size={16} className="vr-spin-inline" /> Loading the library…
          </div>
        )}
        {standards !== null && visible.length === 0 && (
          <div className="vr-empty">
            {standards.length === 0
              ? readOnly
                ? "No standards have been published to the library yet."
                : "No standards yet. Upload one to begin building the library."
              : `No standards in ${domain}.`}
          </div>
        )}
      </GlowCard>


      {uploadOpen && (
        <UploadStandardModal
          initialFile={droppedFile}
          onCancel={() => {
            setUploadOpen(false);
            setDroppedFile(null);
          }}
          onSubmit={submitUpload}
        />
      )}


      {confirming?.kind === "delete" && (
        <ConfirmDialog
          title="Remove this standard?"
          tone="danger"
          confirmLabel="Remove permanently"
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const target = confirming.standard;
            setConfirming(null);
            void remove(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirming.standard.title}</strong> — {confirming.standard.edition}
          </p>
          <p>
            This deletes the uploaded document, the extracted text it produced, and its entire section
            tree
            {confirming.standard.extraction
              ? ` — ${confirming.standard.extraction.sectionCount} sections across ${confirming.standard.extraction.pageCount} pages.`
              : "."}
          </p>
          <p className="vr-confirm-stress">
            This cannot be undone. The document would have to be uploaded and verified again from
            scratch.
          </p>
        </ConfirmDialog>
      )}

      {confirming?.kind === "extract" && (
        <ConfirmDialog
          title="Re-extract this standard?"
          tone="warn"
          confirmLabel="Re-extract"
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const target = confirming.standard;
            setConfirming(null);
            void runExtract(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirming.standard.title}</strong> — {confirming.standard.edition}
          </p>
          <p>
            Every part is read again from its stored file and the section tree is rebuilt from
            scratch, replacing the one there now.
          </p>
          <p className="vr-confirm-stress">
            This can take a few minutes for a large standard. Leave the page open while it runs.
          </p>
          {/* Says plainly what is NOT at risk. The word "re-extract" sounds destructive, and someone
              who is unsure whether it touches their document will simply never press it. */}
          <p className="vr-confirm-calm">
            The uploaded document itself is never modified — only what IQ Verity derived from it.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default StandardsLibrary;
